import { Room } from 'livekit-client';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface MediaPermissions {
    audio: boolean | undefined;
    video: boolean | undefined;
}

interface UseDevices {
    audioInputDevices: MediaDeviceInfo[];
    audioOutputDevices: MediaDeviceInfo[];
    videoDevices: MediaDeviceInfo[];
    permissions: MediaPermissions;
}

export const useDevices = (): UseDevices => {
    const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([]);
    const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
    const [permissions, setPermissions] = useState<MediaPermissions>({ audio: undefined, video: undefined });
    const isListingRef = useRef(false);

    const listDevices = useCallback(async () => {
        if (isListingRef.current) {
            return;
        }

        isListingRef.current = true;

        const [outputResult, inputResult, videoResult] = await Promise.allSettled([
            Room.getLocalDevices('audiooutput', true),
            Room.getLocalDevices('audioinput', true),
            Room.getLocalDevices('videoinput', true),
        ]);

        if (outputResult.status === 'fulfilled') {
            setAudioOutputDevices(outputResult.value);
        } else {
            console.error('Error getting local audiooutput devices', outputResult.reason);
        }

        if (inputResult.status === 'fulfilled') {
            setAudioInputDevices(inputResult.value);
            setPermissions((permissions) => ({ ...permissions, audio: true }));
        } else {
            setPermissions((permissions) => ({ ...permissions, audio: false }));
        }

        if (videoResult.status === 'fulfilled') {
            setVideoDevices(videoResult.value);
            setPermissions((permissions) => ({ ...permissions, video: true }));
        } else {
            setPermissions((permissions) => ({ ...permissions, video: false }));
        }

        isListingRef.current = false;
    }, []);

    useEffect(() => {
        listDevices();
        navigator.mediaDevices.addEventListener('devicechange', listDevices);

        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', listDevices);
        };
    }, []);

    return {
        audioInputDevices,
        audioOutputDevices,
        videoDevices,
        permissions,
    };
};
